import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatBytes } from '../lib/api';
import { 
  Buildings,
  Users,
  Plus,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle
} from '@phosphor-icons/react';

export const AdminPage = () => {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Client dialogs
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [clientForm, setClientForm] = useState({ name: '', description: '', is_active: true });
  
  // User dialogs
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user',
    language: 'it',
    client_id: ''
  });

  const fetchData = async () => {
    try {
      const [clientsRes, usersRes] = await Promise.all([
        api.getClients(),
        api.getUsers()
      ]);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Client handlers
  const openCreateClientDialog = () => {
    setClientForm({ name: '', description: '', is_active: true });
    setEditClient(null);
    setShowClientDialog(true);
  };

  const openEditClientDialog = (client) => {
    setClientForm({
      name: client.name,
      description: client.description || '',
      is_active: client.is_active
    });
    setEditClient(client);
    setShowClientDialog(true);
  };

  const handleSaveClient = async () => {
    if (!clientForm.name.trim()) return;
    try {
      if (editClient) {
        await api.updateClient(editClient.id, clientForm);
      } else {
        await api.createClient(clientForm);
      }
      toast.success(t('common.success'));
      setShowClientDialog(false);
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // User handlers
  const openCreateUserDialog = () => {
    setUserForm({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'user',
      language: 'it',
      client_id: clients[0]?.id || ''
    });
    setEditUser(null);
    setShowUserDialog(true);
  };

  const openEditUserDialog = (user) => {
    setUserForm({
      email: user.email,
      password: '', // Empty - only filled if changing
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      language: user.language || 'it',
      client_id: user.client_id
    });
    setEditUser(user);
    setShowUserDialog(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.first_name || !userForm.last_name) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    if (editUser) {
      // Update existing user
      try {
        const updateData = {
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          role: userForm.role,
          language: userForm.language
        };
        // Only include password if it was changed
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await api.updateUser(editUser.id, updateData);
        toast.success('Utente aggiornato');
        setShowUserDialog(false);
        setEditUser(null);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('common.error'));
      }
    } else {
      // Create new user
      if (!userForm.email || !userForm.password || !userForm.client_id) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }
      try {
        await api.createUser(userForm);
        toast.success(t('common.success'));
        setShowUserDialog(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('common.error'));
      }
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      await api.deleteUser(deleteUser.id);
      toast.success(t('common.success'));
      setDeleteUser(null);
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="admin-title">
          {t('admin.title')}
        </h1>
      </div>

      <div className="content-area">
        <Tabs defaultValue="clients" className="w-full">
          <TabsList className="mb-6 bg-white border border-[#E4E4E7] p-1">
            <TabsTrigger value="clients" className="data-[state=active]:bg-[#09090B] data-[state=active]:text-white">
              <Buildings size={18} className="mr-2" />
              {t('admin.clients')}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#09090B] data-[state=active]:text-white">
              <Users size={18} className="mr-2" />
              {t('admin.users')}
            </TabsTrigger>
          </TabsList>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openCreateClientDialog}
                className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                data-testid="create-client-btn"
              >
                <Plus size={18} className="mr-2" />
                {t('admin.createClient')}
              </Button>
            </div>

            <div className="bg-white border border-[#E4E4E7]">
              {loading ? (
                <div className="p-8 text-center text-[#52525B]">{t('common.loading')}</div>
              ) : (
                <table className="w-full table-swiss">
                  <thead>
                    <tr>
                      <th className="text-left px-6 py-4">{t('admin.clientName')}</th>
                      <th className="text-left px-6 py-4">Status</th>
                      <th className="text-left px-6 py-4">{t('documents.title')}</th>
                      <th className="text-left px-6 py-4">Storage</th>
                      <th className="text-right px-6 py-4">{t('documents.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <tr key={client.id} data-testid={`client-row-${client.id}`}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-[#09090B]">{client.name}</p>
                            {client.description && (
                              <p className="text-xs text-[#52525B]">{client.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {client.is_active ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#0B8043]">
                              <CheckCircle size={14} />
                              {t('admin.active')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#E32636]">
                              <XCircle size={14} />
                              {t('admin.inactive')}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#52525B]">
                          {client.document_count}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#52525B]">
                          {formatBytes(client.storage_used)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => openEditClientDialog(client)}
                            className="p-2 hover:bg-[#F4F4F5] transition-colors"
                            data-testid={`edit-client-${client.id}`}
                          >
                            <PencilSimple size={18} className="text-[#52525B]" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openCreateUserDialog}
                className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                data-testid="create-user-btn"
              >
                <Plus size={18} className="mr-2" />
                {t('admin.createUser')}
              </Button>
            </div>

            <div className="bg-white border border-[#E4E4E7]">
              {loading ? (
                <div className="p-8 text-center text-[#52525B]">{t('common.loading')}</div>
              ) : (
                <table className="w-full table-swiss">
                  <thead>
                    <tr>
                      <th className="text-left px-6 py-4">Nome</th>
                      <th className="text-left px-6 py-4">Email</th>
                      <th className="text-left px-6 py-4">Cliente</th>
                      <th className="text-left px-6 py-4">{t('admin.role')}</th>
                      <th className="text-right px-6 py-4">{t('documents.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td className="px-6 py-4 text-sm font-medium text-[#09090B]">
                          {user.first_name} {user.last_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#52525B]">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#52525B]">
                          {user.client_name}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-[#E3F2FD] text-[#002FA7]' 
                              : user.role === 'approver'
                              ? 'bg-[#E8F5E9] text-[#2E7D32]'
                              : 'bg-[#F4F4F5] text-[#52525B]'
                          }`}>
                            {t(`admin.roles.${user.role}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => openEditUserDialog(user)}
                            className="p-2 hover:bg-[#F4F4F5] transition-colors"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <PencilSimple size={18} className="text-[#52525B]" />
                          </button>
                          <button
                            onClick={() => setDeleteUser(user)}
                            className="p-2 hover:bg-[#FFEBEE] transition-colors"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash size={18} className="text-[#E32636]" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Client Dialog */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editClient ? t('common.edit') : t('admin.createClient')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('admin.clientName')} *
              </Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                className="input-swiss mt-1"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('folders.description')}
              </Label>
              <Textarea
                value={clientForm.description}
                onChange={(e) => setClientForm({ ...clientForm, description: e.target.value })}
                className="input-swiss mt-1"
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-[#09090B]">{t('admin.active')}</Label>
              <Switch
                checked={clientForm.is_active}
                onCheckedChange={(checked) => setClientForm({ ...clientForm, is_active: checked })}
                data-testid="client-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientDialog(false)} className="rounded-sm">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveClient}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="save-client-btn"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editUser ? 'Modifica Utente' : t('admin.createUser')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  Nome *
                </Label>
                <Input
                  value={userForm.first_name}
                  onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                  className="input-swiss mt-1"
                  data-testid="user-firstname-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  Cognome *
                </Label>
                <Input
                  value={userForm.last_name}
                  onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                  className="input-swiss mt-1"
                  data-testid="user-lastname-input"
                />
              </div>
            </div>
            
            {!editUser && (
              <>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="input-swiss mt-1"
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                    Cliente *
                  </Label>
                  <Select 
                    value={userForm.client_id} 
                    onValueChange={(v) => setUserForm({ ...userForm, client_id: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="user-client-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {editUser && (
              <div className="bg-[#F4F4F5] p-3 rounded">
                <p className="text-sm text-[#52525B]">
                  <span className="font-medium">Email:</span> {editUser.email}
                </p>
                <p className="text-sm text-[#52525B]">
                  <span className="font-medium">Cliente:</span> {editUser.client_name}
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {editUser ? 'Nuova Password (lascia vuoto per non modificare)' : 'Password *'}
              </Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                className="input-swiss mt-1"
                placeholder={editUser ? '••••••••' : ''}
                data-testid="user-password-input"
              />
            </div>
            
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('admin.role')} *
              </Label>
              <Select 
                value={userForm.role} 
                onValueChange={(v) => setUserForm({ ...userForm, role: v })}
              >
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t('admin.roles.user')}</SelectItem>
                  <SelectItem value="approver">{t('admin.roles.approver')}</SelectItem>
                  <SelectItem value="admin">{t('admin.roles.admin')}</SelectItem>
                </SelectContent>
              </Select>
              {userForm.role === 'approver' && (
                <p className="text-xs text-[#52525B] mt-1">
                  L'approvatore può approvare/rifiutare documenti nel workflow
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)} className="rounded-sm">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveUser}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="save-user-btn"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare l'utente {deleteUser?.email}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-[#E32636] hover:bg-[#C62828] text-white"
              data-testid="confirm-delete-user"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};
